import { Component, Input } from '@angular/core'
import { CostInvoiceRead, InvoicesService, OrderRead, OrderReadListItem, OrdersService, TaskRead } from '@app/generated'
import { NotificationService } from '@core/services/notification.service'
import { AutocompleteComponent } from '@core/components/autocomplete/autocomplete.component'
import { FormFieldComponent } from '@core/components/form-field/form-field.component'
import { NgIf } from '@angular/common'
import { OptionComponent } from '@core/components/option/option.component'
import { FormControl, FormGroup } from '@angular/forms'
import { ButtonComponent } from '@core/components/button/button.component'
import { config } from '@config'
import { ModalService } from '@core/services/modal.service'
import { Router } from '@angular/router'
import { forkJoin } from 'rxjs'

export interface assignCostToOrderForm {
	order: FormControl<OrderRead | null>
	task: FormControl<TaskRead | null>
}

@Component({
	selector: 'assign-cost-to-order',
	templateUrl: './assign-cost-to-order.component.html',
	styleUrl: './assign-cost-to-order.component.scss',
	standalone: true,
	imports: [AutocompleteComponent, FormFieldComponent, NgIf, OptionComponent, ButtonComponent],
})
export class AssignCostToOrder {
	orders: OrderReadListItem[] = []
	tasks: TaskRead[] = []
	@Input() invoice?: CostInvoiceRead

	form: FormGroup<assignCostToOrderForm> = new FormGroup<assignCostToOrderForm>({
		order: new FormControl(null),
		task: new FormControl(null),
	})

	protected readonly textChooseOrder = $localize`Choose order`
	protected readonly textChooseTask = $localize`Choose task`
	protected readonly textUpdateInvoice = $localize`Update invoice`
	protected readonly textAssign = $localize`Assign`
	protected readonly textUnassign = $localize`Unassign`

	constructor(
		private invoicesService: InvoicesService,
		private ordersService: OrdersService,
		private notificationService: NotificationService,
		private modalService: ModalService,
		private router: Router,
	) {}

	ngOnInit() {
		this.handleOrderSearchValueChanges('')
		this.fetchInvoice()

		this.form.controls.order.valueChanges.subscribe(value => {
			if (value) {
				this.handleOrderTaskSearchValueChanges('', this.form.controls.order.value?.id)
			}
		})
	}

	fetchInvoice() {
		if (!this.invoice) {
			return
		}
		forkJoin({
			invoice: this.invoicesService.getCostInvoice(this.invoice!.id),
		})
			.pipe()
			.subscribe({
				next: result => {
					this.invoice = result.invoice
				},
			})
	}

	handleOrderSearchValueChanges(value: string) {
		this.ordersService.getOrders(0, 10, value, false, 'desc', 'created_at').subscribe({
			next: response => {
				this.orders = response.items
			},
			error: () => {
				this.notificationService.error($localize`Failed to fetch entities`)
			},
		})
	}
	onOrderSelect() {
		if (this.form.controls.task) {
			this.form.controls.task.setValue(null)
		}
	}

	handleOrderTaskSearchValueChanges(value: string, orderId: string | undefined) {
		if (orderId) {
			this.ordersService.getOrderTasks(orderId, 0, 10, value, false, 'desc', 'created_at').subscribe({
				next: response => {
					this.tasks = response.items
				},
				error: () => {
					this.notificationService.error($localize`Failed to fetch entities`)
				},
			})
		}
	}

	async submit() {
		if (this.form.invalid) {
			this.notificationService.error($localize`Please select invoice`)
			return
		}
		if (this.invoice?.is_group_invoice) {
			await this.router.navigateByUrl(`/${config.modules.costInvoices.path}/${this.invoice?.id}/update`)
		} else {
			const data = this.form.value.task ? { task_id: this.form.value.task.id } : { order_id: this.form.value.order!.id }
			this.invoicesService.assignToInvoice_1(this.invoice!.id, data).subscribe({
				next: () => {
					this.modalService.modalRef.close()
					this.notificationService.success($localize`Cost invoice has been assigned`)
				},
				error: () => {
					this.notificationService.error($localize`Was not able to assign cost invoice`)
				},
			})
		}
	}

	unassignInvoice() {
		const data =
			this.invoice?.orders_minimal?.[0]?.tasks_ids && this.invoice?.orders_minimal?.[0]?.tasks_ids.length > 0
				? { task_ids: this.invoice?.orders_minimal?.[0]?.tasks_ids }
				: { order_id: this.invoice?.orders_minimal?.[0]?.order_id }
		this.invoicesService.unassignFromCostInvoice(this.invoice!.id, data).subscribe({
			next: () => {
				if (this.invoice?.orders_minimal) {
					this.invoice.orders_minimal = undefined
				}
				this.notificationService.success($localize`Successfully unassigned cost invoice`)
			},
			error: () => {
				this.notificationService.error($localize`Failed to unassign cost invoice`)
			},
		})
	}
}
